import { useMemo, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateField,
  useCreateStatus,
  useDeleteField,
  useDeleteStatus,
  useFields,
  useReorderFields,
  useReorderStatuses,
  useStatuses,
  useUpdateField,
  useUpdateStatus,
  useTestingFlow,
  useUpdateTestingFlow,
} from "@/api/work";
import type { FieldDef } from "@/api/types/FieldDef";
import type { FieldOption } from "@/api/types/FieldOption";
import type { FieldType } from "@/api/types/FieldType";
import type { Project } from "@/api/types/Project";
import type { StatusCategory } from "@/api/types/StatusCategory";
import type { StatusColor } from "@/api/types/StatusColor";
import type { TaskStatus } from "@/api/types/TaskStatus";
import type { EstimateUnit } from "@/api/types/EstimateUnit";
import type { SprintSettings } from "@/api/types/SprintSettings";
import type { UpdateSprintSettingsRequest } from "@/api/types/UpdateSprintSettingsRequest";
import type { Weekday } from "@/api/types/Weekday";
import { useUpdateProject } from "@/api/hooks";
import { useSprints, useSprintSettings, useUpdateSprintSettings } from "@/api/sprints";
import { Combobox } from "@/components/Combobox";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { CategoryDot, StatusDot } from "@/components/Status";
import { Popover } from "@/components/work/Popover";
import { useToast } from "@/components/Toast";
import { Button, cx, Field, Input, Select } from "@/components/ui";
import { PALETTE } from "@/lib/work";
import { columnTone, STATUS_COLORS } from "@/lib/statusColors";
import { allZones, browserZone, WEEKDAYS } from "@/lib/sprints";

const CATEGORIES: StatusCategory[] = ["not_started", "active", "done", "closed"];
const FIELD_TYPES: FieldType[] = ["text", "number", "date", "checkbox", "single_select", "user"];

function Card({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex flex-col gap-1">
        <h3 className="m-0 label-caps">{title}</h3>
        <p className="m-0 text-sm text-muted">{intro}</p>
      </div>
      {children}
    </section>
  );
}

/** Drag rows to reorder; the full new order is sent at once. */
function useRowDrag(ids: string[], onOrder: (ids: string[]) => void) {
  const [dragging, setDragging] = useState<string | null>(null);
  return (id: string) => ({
    draggable: true,
    onDragStart: (e: DragEvent) => {
      setDragging(id);
      e.dataTransfer.effectAllowed = "move";
    },
    onDragEnd: () => setDragging(null),
    onDragOver: (e: DragEvent) => dragging && e.preventDefault(),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      if (!dragging || dragging === id) return;
      const next = ids.filter((x) => x !== dragging);
      next.splice(next.indexOf(id) + (ids.indexOf(dragging) < ids.indexOf(id) ? 1 : 0), 0, dragging);
      onOrder(next);
      setDragging(null);
    },
  });
}

// ───────────── Statuses ─────────────

export function StatusesSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const statuses = useStatuses(project.key);
  const create = useCreateStatus(project.key);
  const reorder = useReorderStatuses(project.key);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<StatusCategory>("active");
  const list = statuses.data ?? [];
  const drag = useRowDrag(
    list.map((s) => s.id),
    (ids) => reorder.mutate(ids, { onError: (e) => toast.error(e) }),
  );

  return (
    <Card title={t("workSettings.statuses")} intro={t("workSettings.statusesIntro")}>
      <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-ctl)] border border-border p-0">
        {list.map((s) => (
          <StatusRow key={s.id} project={project} status={s} all={list} dragProps={drag(s.id)} />
        ))}
      </ul>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate({ name: name.trim(), category }, { onSuccess: () => setName(""), onError: (err) => toast.error(err) });
        }}
      >
        <Field label={t("workSettings.newStatus")}>
          {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className="w-56" />}
        </Field>
        <Field label={t("workSettings.category")}>
          {(p) => (
            <Select {...p} value={category} onChange={(e) => setCategory(e.target.value as StatusCategory)} className="w-44">
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`work.category.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Button type="submit" icon="plus" loading={create.isPending} disabled={!name.trim()}>
          {t("common.add")}
        </Button>
      </form>
      <TestingFlowSettings project={project} statuses={list} />
    </Card>
  );
}

/** Which status is the board's Test column, and where a task goes when a round passes. */
function TestingFlowSettings({ project, statuses }: { project: Project; statuses: TaskStatus[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const flow = useTestingFlow(project.key);
  const update = useUpdateTestingFlow(project.key);
  if (!flow.data) return null;
  const testing = flow.data.testing_status_id;
  const onPass = flow.data.on_pass_status_id;
  const open = statuses.filter((s) => s.category === "not_started" || s.category === "active");
  // What "next status" means right now, so the choice reads plainly.
  const at = statuses.findIndex((s) => s.id === testing);
  const next = at >= 0 ? statuses[at + 1] : undefined;
  const save = (body: { testing_status_id: string | null; on_pass_status_id: string | null }) =>
    update.mutate(body, { onSuccess: () => toast.success(t("workSettings.testingSaved")), onError: (e) => toast.error(e) });

  return (
    <div className="flex flex-col gap-3 border-t border-border pt-4">
      <div>
        <h3 className="m-0 text-sm font-semibold">{t("workSettings.testingFlow")}</h3>
        <p className="m-0 mt-1 text-sm text-muted">{t("workSettings.testingFlowIntro")}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t("workSettings.testingStatus")}>
          {(p) => (
            <Select
              {...p}
              value={testing ?? ""}
              disabled={update.isPending}
              onChange={(e) => save({ testing_status_id: e.target.value || null, on_pass_status_id: onPass === e.target.value ? null : onPass })}
              className="w-56"
            >
              <option value="">{t("workSettings.testingNone")}</option>
              {open.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {testing && (
          <Field label={t("workSettings.onPass")}>
            {(p) => (
              <Select
                {...p}
                value={onPass ?? ""}
                disabled={update.isPending}
                onChange={(e) => save({ testing_status_id: testing, on_pass_status_id: e.target.value || null })}
                className="w-56"
              >
                <option value="">{next ? t("workSettings.onPassNext", { name: next.name }) : t("workSettings.onPassStay")}</option>
                {statuses
                  .filter((s) => s.id !== testing)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        )}
      </div>
    </div>
  );
}

/** The status's dot, as a button: pick its board colour from the fixed palette, or go back to the category's. */
function StatusColorPicker({
  status,
  disabled,
  onPick,
}: {
  status: TaskStatus;
  disabled: boolean;
  onPick: (color: StatusColor | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <Popover
      width={196}
      trigger={({ toggle, props }) => (
        <button
          type="button"
          {...props}
          onClick={toggle}
          disabled={disabled}
          aria-label={t("workSettings.colorFor", { name: status.name })}
          title={status.color ? t(`workSettings.colors.${status.color}`) : t("workSettings.colorDefault")}
          className="inline-flex size-6 items-center justify-center rounded-md hover:bg-surface-2"
        >
          <StatusDot category={status.category} color={status.color} className="size-3" />
        </button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-2 p-2.5">
          <div className="grid grid-cols-5 gap-1.5">
            {STATUS_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={t(`workSettings.colors.${c}`)}
                aria-pressed={status.color === c}
                title={t(`workSettings.colors.${c}`)}
                onClick={() => {
                  if (status.color !== c) onPick(c);
                  close();
                }}
                className={cx(
                  "flex size-7 items-center justify-center rounded-full ring-offset-2 ring-offset-surface",
                  columnTone({ category: status.category, color: c }).dot,
                  status.color === c ? "ring-2 ring-text" : "hover:ring-2 hover:ring-border-strong",
                )}
              >
                {status.color === c && <Icon name="check" size={13} className="text-white" />}
              </button>
            ))}
          </div>
          <button
            type="button"
            aria-pressed={!status.color}
            onClick={() => {
              if (status.color) onPick(null);
              close();
            }}
            className={cx(
              "flex items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm hover:bg-surface-2",
              !status.color && "font-semibold",
            )}
          >
            <CategoryDot category={status.category} />
            {t("workSettings.colorDefault")}
          </button>
        </div>
      )}
    </Popover>
  );
}

function StatusRow({
  project,
  status,
  all,
  dragProps,
}: {
  project: Project;
  status: TaskStatus;
  all: TaskStatus[];
  dragProps: ReturnType<ReturnType<typeof useRowDrag>>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateStatus(project.key);
  const remove = useDeleteStatus(project.key);
  const [name, setName] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  const others = all.filter((s) => s.id !== status.id);
  const err = { onError: (e: unknown) => toast.error(e) };

  return (
    <li {...dragProps} className="flex flex-wrap items-center gap-2 px-3 py-2">
      <Icon name="grip" size={14} className="cursor-grab text-muted" />
      <StatusColorPicker
        status={status}
        disabled={update.isPending}
        onPick={(color) => update.mutate({ id: status.id, name: null, category: null, color }, err)}
      />
      {name === null ? (
        <button type="button" className="min-w-0 flex-1 truncate text-left font-medium" onClick={() => setName(status.name)}>
          {status.name}
        </button>
      ) : (
        <Input
          autoFocus
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            if (name.trim() && name.trim() !== status.name) update.mutate({ id: status.id, name: name.trim(), category: null }, err);
            setName(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setName(null);
          }}
          className="h-8 flex-1"
        />
      )}
      <span className="text-xs text-muted">{t("workSettings.nTasks", { count: status.task_count })}</span>
      <Select
        value={status.category}
        onChange={(e) => update.mutate({ id: status.id, name: null, category: e.target.value as StatusCategory }, err)}
        aria-label={t("workSettings.category")}
        className="h-8 w-auto text-sm"
      >
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {t(`work.category.${c}`)}
          </option>
        ))}
      </Select>
      {status.category === "active" && (
        <label className="flex items-center gap-1 text-xs text-muted" title={t("workSettings.wipHint")}>
          {t("workSettings.wip")}
          <input
            key={status.wip_limit ?? "none"}
            type="number"
            min={1}
            max={999}
            defaultValue={status.wip_limit ?? ""}
            placeholder="—"
            aria-label={t("workSettings.wipFor", { name: status.name })}
            onBlur={(e) => {
              const v = e.target.value.trim();
              const next = v === "" ? null : Math.round(Number(v));
              if (next !== null && !(next >= 1 && next <= 999)) {
                e.target.value = status.wip_limit == null ? "" : String(status.wip_limit);
                return;
              }
              if (next !== status.wip_limit) update.mutate({ id: status.id, name: null, category: null, wip_limit: next }, err);
            }}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className="h-8 w-16 rounded-md border border-border-strong bg-surface px-2 text-sm text-text"
          />
        </label>
      )}
      <Button size="sm" variant="ghost" icon="trash" aria-label={t("common.delete")} onClick={() => setDeleting(true)} disabled={all.length <= 1} />
      {status.task_count > 0 ? (
        <Dialog
          open={deleting}
          onClose={() => setDeleting(false)}
          title={t("workSettings.deleteStatus", { name: status.name })}
          footer={
            <>
              <Button onClick={() => setDeleting(false)}>{t("common.cancel")}</Button>
              <Button
                variant="danger"
                disabled={!moveTo}
                loading={remove.isPending}
                onClick={() => remove.mutate({ id: status.id, moveTo }, { onSuccess: () => setDeleting(false), ...err })}
              >
                {t("common.delete")}
              </Button>
            </>
          }
        >
          <p className="m-0 text-text-2">{t("workSettings.moveTasks", { count: status.task_count })}</p>
          <Select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} aria-label={t("workSettings.moveTo")}>
            <option value="">—</option>
            {others.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Dialog>
      ) : (
        <ConfirmDialog
          open={deleting}
          onClose={() => setDeleting(false)}
          danger
          title={t("workSettings.deleteStatus", { name: status.name })}
          body={t("workSettings.deleteStatusBody")}
          confirmLabel={t("common.delete")}
          loading={remove.isPending}
          onConfirm={() => remove.mutate({ id: status.id }, { onSuccess: () => setDeleting(false), ...err })}
        />
      )}
    </li>
  );
}

// ───────────── Custom fields ─────────────

export function FieldsSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const fields = useFields(project.key);
  const reorder = useReorderFields(project.key);
  const remove = useDeleteField(project.key);
  const [editing, setEditing] = useState<FieldDef | "new" | null>(null);
  const [deleting, setDeleting] = useState<FieldDef | null>(null);
  const list = fields.data ?? [];
  const drag = useRowDrag(
    list.map((f) => f.id),
    (ids) => reorder.mutate(ids, { onError: (e) => toast.error(e) }),
  );
  return (
    <Card title={t("workSettings.fields")} intro={t("workSettings.fieldsIntro")}>
      {list.length > 0 && (
        <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-ctl)] border border-border p-0">
          {list.map((f) => (
            <li key={f.id} {...drag(f.id)} className="flex items-center gap-2 px-3 py-2">
              <Icon name="grip" size={14} className="cursor-grab text-muted" />
              <span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
              <span className="text-xs text-muted">{t(`workSettings.type.${f.field_type}`)}</span>
              <Button size="sm" variant="ghost" icon="edit" aria-label={t("common.edit")} onClick={() => setEditing(f)} />
              <Button size="sm" variant="ghost" icon="trash" aria-label={t("common.delete")} onClick={() => setDeleting(f)} />
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button icon="plus" onClick={() => setEditing("new")}>
          {t("workSettings.addField")}
        </Button>
      </div>
      {editing && <FieldDialog project={project} field={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        danger
        title={t("workSettings.deleteField", { name: deleting?.name ?? "" })}
        body={t("workSettings.deleteFieldBody")}
        confirmLabel={t("common.delete")}
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null), onError: (e) => toast.error(e) })}
      />
    </Card>
  );
}

function FieldDialog({ project, field, onClose }: { project: Project; field: FieldDef | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreateField(project.key);
  const update = useUpdateField(project.key);
  const [name, setName] = useState(field?.name ?? "");
  const [type, setType] = useState<FieldType>(field?.field_type ?? "text");
  const [options, setOptions] = useState<FieldOption[]>(field?.options ?? [{ id: "", name: "", color: PALETTE[0]! }]);
  const select = type === "single_select";
  const valid = name.trim() && (!select || options.some((o) => o.name.trim()));
  const clean = () => (select ? options.filter((o) => o.name.trim()).map((o) => ({ ...o, name: o.name.trim() })) : []);
  const save = () => {
    const done = { onSuccess: onClose, onError: (e: unknown) => toast.error(e) };
    if (field) update.mutate({ id: field.id, name: name.trim(), options: select ? clean() : null }, done);
    else create.mutate({ name: name.trim(), field_type: type, options: clean() }, done);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={field ? t("workSettings.editField") : t("workSettings.addField")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!valid} loading={create.isPending || update.isPending} onClick={save}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field label={t("workSettings.fieldName")}>{(p) => <Input {...p} autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />}</Field>
      <Field label={t("workSettings.fieldType")} hint={field ? t("workSettings.typeFixed") : undefined}>
        {(p) => (
          <Select {...p} value={type} disabled={!!field} onChange={(e) => setType(e.target.value as FieldType)}>
            {FIELD_TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`workSettings.type.${x}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {select && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text-2">{t("workSettings.options")}</span>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="color"
                value={o.color ?? PALETTE[i % PALETTE.length]!}
                onChange={(e) => setOptions(options.map((x, j) => (j === i ? { ...x, color: e.target.value } : x)))}
                aria-label={t("workSettings.color")}
                className="h-8 w-9 cursor-pointer rounded border border-border-strong bg-surface p-0.5"
              />
              <Input value={o.name} onChange={(e) => setOptions(options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} maxLength={60} className="h-8" />
              <Button size="sm" variant="ghost" icon="x" aria-label={t("common.remove")} onClick={() => setOptions(options.filter((_, j) => j !== i))} />
            </div>
          ))}
          <div>
            <Button size="sm" icon="plus" onClick={() => setOptions([...options, { id: "", name: "", color: PALETTE[options.length % PALETTE.length]! }])}>
              {t("workSettings.addOption")}
            </Button>
          </div>
          {field && <p className={cx("m-0 text-xs text-muted")}>{t("workSettings.optionRemoveHint")}</p>}
        </div>
      )}
    </Dialog>
  );
}

// ───────────── Sprints ─────────────

/** The project's scrum switch. Off hides every scrum feature and pauses automatic sprints; nothing is deleted. */
export function ScrumSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateProject(project.key);
  return (
    <Card title={t("sprintSettings.scrumTitle")} intro={t("sprintSettings.scrumIntro")}>
      <label className={cx("flex items-start gap-2.5", update.isPending ? "cursor-not-allowed opacity-60" : "cursor-pointer")}>
        <input
          type="checkbox"
          role="switch"
          className="mt-0.5 size-4 accent-[var(--accent)]"
          checked={project.scrum_enabled}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ scrum_enabled: e.target.checked }, { onError: (err) => toast.error(err) })}
        />
        <span className="flex flex-col">
          <span className="text-sm font-medium">{t("sprintSettings.scrumLabel")}</span>
          <span className="text-xs text-muted">{t("sprintSettings.scrumHint")}</span>
        </span>
      </label>
    </Card>
  );
}

/**
 * Estimate unit, sprint length, timezone, working days, time logging and automatic sprints. The unit is locked while a
 * sprint is active; turning automatic sprints on plans the next sprint right away. With scrum off only the timezone
 * remains, which recurring tasks and flow charts also use.
 */
export function SprintSettingsSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const settings = useSprintSettings(project.key);
  const scrum = project.scrum_enabled;
  return (
    <Card
      title={scrum ? t("sprintSettings.title") : t("sprintSettings.timezoneTitle")}
      intro={scrum ? t("sprintSettings.intro") : t("sprintSettings.timezoneIntro")}
    >
      {settings.isPending ? (
        <span className="text-sm text-muted">{t("common.loading")}</span>
      ) : settings.isError ? (
        <Button size="sm" onClick={() => void settings.refetch()}>
          {t("common.retry")}
        </Button>
      ) : (
        <SprintSettingsForm key={JSON.stringify(settings.data)} project={project} settings={settings.data} />
      )}
    </Card>
  );
}

function SprintSettingsForm({ project, settings }: { project: Project; settings: SprintSettings }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateSprintSettings(project.key);
  const scrum = project.scrum_enabled;
  const sprints = useSprints(project.key, scrum);
  const zones = useMemo(allZones, []);
  const browser = browserZone();
  // A project still on the server default starts from the browser's zone; it applies once saved.
  const suggested = settings.timezone === "UTC" && browser !== "UTC";
  const [unit, setUnit] = useState<EstimateUnit>(settings.estimate_unit);
  const [weeks, setWeeks] = useState(settings.sprint_weeks);
  const [zone, setZone] = useState(suggested ? browser : settings.timezone);
  const [days, setDays] = useState<Weekday[]>(settings.working_days);
  const [timeLogging, setTimeLogging] = useState(settings.time_logging);
  const [auto, setAuto] = useState(settings.auto_sprints);
  const [weekday, setWeekday] = useState<Weekday>(settings.auto_weekday);
  const zoneOk = zones.includes(zone);
  const body: UpdateSprintSettingsRequest = {};
  if (unit !== settings.estimate_unit) body.estimate_unit = unit;
  if (weeks !== settings.sprint_weeks) body.sprint_weeks = weeks;
  if (zone !== settings.timezone) body.timezone = zone;
  if (days.join() !== settings.working_days.join()) body.working_days = days;
  if (timeLogging !== settings.time_logging) body.time_logging = timeLogging;
  if (auto !== settings.auto_sprints) body.auto_sprints = auto;
  if (weekday !== settings.auto_weekday) body.auto_weekday = weekday;
  const dirty = Object.keys(body).length > 0;
  const nextN = (sprints.data ?? []).reduce((m, s) => Math.max(m, s.number), 0) + 1;
  const hasPlanned = (sprints.data ?? []).some((s) => s.state === "planned");
  const toggleDay = (d: Weekday) => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || cur.includes(x))));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!dirty || !zoneOk || days.length === 0) return;
    update.mutate(body, { onSuccess: () => toast.success(t("settings.saved")), onError: (err) => toast.error(err) });
  };

  const switchRow = (label: string, hint: string, checked: boolean, onChange: (v: boolean) => void, disabled?: boolean) => (
    <label className={cx("flex items-start gap-2.5", disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer")}>
      <input type="checkbox" role="switch" className="mt-0.5 size-4 accent-[var(--accent)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="flex flex-col">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted">{hint}</span>
      </span>
    </label>
  );

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {scrum && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("sprintSettings.unit")}
            hint={settings.has_active_sprint ? t("sprintSettings.unitLocked") : t("sprintSettings.unitHint")}
          >
            {(p) => (
              <Select {...p} value={unit} disabled={settings.has_active_sprint} onChange={(e) => setUnit(e.target.value as EstimateUnit)}>
                {(["points", "hours", "none"] as const).map((u) => (
                  <option key={u} value={u}>
                    {t(`sprintSettings.units.${u}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t("sprintSettings.length")}>
            {(p) => (
              <Select {...p} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
                {[1, 2, 3, 4].map((w) => (
                  <option key={w} value={w}>
                    {t("sprintSettings.weeks", { count: w })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}
      <Field
        label={t("sprintSettings.timezone")}
        error={!zoneOk ? t("sprintSettings.timezoneUnknown") : undefined}
        hint={suggested && zone === browser ? t("sprintSettings.timezoneSuggested") : scrum ? t("sprintSettings.timezoneHint") : undefined}
      >
        {(p) => <Combobox {...p} value={zone} onChange={setZone} options={zones} maxLength={64} />}
      </Field>
      {scrum && (
        <>
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 text-sm font-medium text-text-2">{t("sprintSettings.workingDays")}</legend>
          <div className="flex flex-wrap gap-1">
            {WEEKDAYS.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={days.includes(d)}
                onClick={() => toggleDay(d)}
                className={cx(
                  "h-8 min-w-11 rounded-[var(--radius-ctl)] border px-2 text-sm",
                  days.includes(d) ? "border-accent bg-accent-soft font-semibold text-accent" : "border-border-strong text-muted hover:bg-surface-2",
                )}
              >
                {t(`sprintSettings.day.${d}`)}
              </button>
            ))}
          </div>
          <span className={cx("text-xs", days.length ? "text-muted" : "text-fail-fg")}>
            {days.length ? t("sprintSettings.workingDaysHint") : t("sprintSettings.workingDaysNone")}
          </span>
        </fieldset>
        {switchRow(
          t("sprintSettings.timeLogging"),
          unit === "hours" ? t("sprintSettings.timeLoggingHint") : t("sprintSettings.timeLoggingHoursOnly"),
          timeLogging,
          setTimeLogging,
          unit !== "hours",
        )}
        {switchRow(t("sprintSettings.auto"), t("sprintSettings.autoHint"), auto, setAuto)}
        {auto && (
          <div className="ml-6 flex flex-col gap-2 rounded-[var(--radius-ctl)] border border-border p-3">
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t("sprintSettings.autoWeekday")}>
                {(p) => (
                  <Select {...p} value={weekday} onChange={(e) => setWeekday(e.target.value as Weekday)} className="w-44">
                    {WEEKDAYS.map((d) => (
                      <option key={d} value={d}>
                        {t(`sprintSettings.dayLong.${d}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <span className="pb-2 text-sm text-muted">{t("sprintSettings.autoLength", { count: weeks })}</span>
            </div>
            <span className="text-xs text-muted">
              {!settings.auto_sprints && !hasPlanned ? t("sprintSettings.autoWillPlan", { name: `${project.key} Sprint ${nextN}` }) : t("sprintSettings.autoKeeps")}
            </span>
          </div>
        )}
        </>
      )}
      <div className="flex items-center gap-3">
        <Button type="submit" variant="primary" loading={update.isPending} disabled={!dirty || !zoneOk || days.length === 0}>
          {t("common.save")}
        </Button>
        {dirty && <span className="text-xs text-muted">{t("sprintSettings.unsaved")}</span>}
      </div>
    </form>
  );
}
